import { Link } from 'react-router-dom';
import { format, formatDistanceToNow } from 'date-fns';
import { useTranslation } from 'react-i18next';
import {
  Receipt,
  Calendar,
  FileText,
  User,
  Wallet,
  Clock,
  Hash,
  CreditCard,
  Percent,
  FileCheck,
  CircleDollarSign,
  CalendarClock,
  BadgeCheck,
} from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { InfoField } from '@/components/common/InfoField';
import { QuickLinkButton } from '@/components/common/QuickLinkButton';
import { formatCurrency } from '@/lib/utils/currency';
import { PaymentStatusBadge } from './PaymentStatusBadge';
import type { Bill } from '../types';

export function formatBillType(type?: string | null): string {
  if (!type) return '-';
  return type
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

interface BillCardProps {
  bill: Bill;
}

export function BillDetailsCard({ bill }: BillCardProps) {
  const { t } = useTranslation();
  const formattedTotal = formatCurrency(bill.total, {
    locale: 'uz-UZ',
    currency: bill.currency,
  });
  const formattedPaid = formatCurrency(bill.paid, {
    locale: 'uz-UZ',
    currency: bill.currency,
  });
  const formattedTotalBeforeDiscount = formatCurrency(bill.totalWithoutDiscount || bill.total, {
    locale: 'uz-UZ',
    currency: bill.currency,
  });

  return (
    <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
      <CardHeader className="pb-3 border-b border-slate-100">
        <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Receipt className="h-5 w-5 text-slate-400" />
          {t('billInformation')}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        <InfoField
          icon={CircleDollarSign}
          label={t('total')}
          value={formattedTotal}
        />
        {bill.discount > 0 && (
          <>
            <InfoField
              icon={Percent}
              label={t('discount')}
              value={`${bill.discount}%`}
            />
            <InfoField
              icon={CircleDollarSign}
              label={t('totalBeforeDiscount')}
              value={formattedTotalBeforeDiscount}
            />
          </>
        )}
        <InfoField
          icon={Wallet}
          label={t('paidAmount')}
          value={formattedPaid}
        />
        {bill.description && (
          <InfoField
            icon={FileText}
            label={t('description')}
            value={bill.description}
          />
        )}
      </CardContent>
    </Card>
  );
}

export function BillContractCard({ bill }: BillCardProps) {
  const { t } = useTranslation();
  if (!bill.contract) return null;

  return (
    <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
      <CardHeader className="pb-3 border-b border-slate-100">
        <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <FileCheck className="h-5 w-5 text-slate-400" />
          {t('contractInformation')}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        <div className="flex items-start gap-3">
          <Hash className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
              {t('contract')}
            </p>
            <Link
              to={`/app/contracts/${bill.contract.id}`}
              className="text-sm font-medium text-blue-600 hover:underline"
            >
              {bill.contract.name || `#${bill.contract.id}`}
            </Link>
          </div>
        </div>
        {bill.contract.description && (
          <InfoField
            icon={FileText}
            label={t('description')}
            value={bill.contract.description}
          />
        )}
        {bill.contract.type && (
          <InfoField
            icon={FileCheck}
            label={t('type')}
            value={formatBillType(bill.contract.type)}
          />
        )}
        {bill.contract.status && (
          <div className="flex items-start gap-3">
            <BadgeCheck className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
                {t('status.status')}
              </p>
              <Badge variant="outline" className="text-xs">
                {t(`status.${bill.contract.status}`)}
              </Badge>
            </div>
          </div>
        )}
        {bill.contract.client && (
          <div className="flex items-start gap-3">
            <User className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium uppercase tracking-wider text-slate-500 mb-1">
                {t('client')}
              </p>
              <Link
                to={`/app/clients/${bill.contract.client.id}`}
                className="text-sm font-medium text-blue-600 hover:underline"
              >
                {bill.contract.client.name || t('unnamed')}
              </Link>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function BillSystemInfoCard({ bill }: BillCardProps) {
  const { t } = useTranslation();
  const isPaid = bill.state === 'PAID';
  const isOverdue = !isPaid && bill.dueAt && new Date(bill.dueAt) < new Date();

  return (
    <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow">
      <CardHeader className="pb-3 border-b border-slate-100">
        <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Clock className="h-5 w-5 text-slate-400" />
          {t('systemInfo')}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        <InfoField label={t('billId')} value={String(bill.id)} mono />
        <InfoField
          icon={CalendarClock}
          label={t('dueDate')}
          value={
            bill.dueAt ? (
              <span className={isOverdue ? 'text-red-600' : ''}>
                {format(new Date(bill.dueAt), 'MMM d, yyyy HH:mm')}
                {isOverdue && (
                  <span className="text-red-500 text-xs ml-2">
                    ({t('overdue')})
                  </span>
                )}
              </span>
            ) : (
              '-'
            )
          }
        />
        {bill.paidAt && (
          <InfoField
            icon={Calendar}
            label={t('paidAt')}
            value={format(new Date(bill.paidAt), 'MMM d, yyyy HH:mm')}
          />
        )}
        <InfoField
          label={t('createdAt')}
          value={
            bill.createdAt
              ? format(new Date(bill.createdAt), 'MMM d, yyyy HH:mm')
              : '-'
          }
        />
        <InfoField
          label={t('updatedAt')}
          value={
            bill.updatedAt ? (
              <span>
                {format(new Date(bill.updatedAt), 'MMM d, yyyy HH:mm')}
                <span className="text-slate-400 text-xs ml-2">
                  ({formatDistanceToNow(new Date(bill.updatedAt), { addSuffix: true })})
                </span>
              </span>
            ) : (
              '-'
            )
          }
        />
      </CardContent>
    </Card>
  );
}

export function PaymentsCard({ bill }: BillCardProps) {
  const { t } = useTranslation();
  return (
    <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow md:col-span-2 lg:col-span-3">
      <CardHeader className="pb-3 border-b border-slate-100">
        <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-slate-400" />
          {t('payments')}
          {bill.payments && bill.payments.length > 0 && (
            <span className="ml-2 bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded-full">
              {bill.payments.length}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        {bill.payments && bill.payments.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {bill.payments.map((payment) => (
              <Link
                key={payment.id}
                to={`/app/payments/${payment.id}`}
                className="block"
              >
                <div className="border border-slate-200 rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50/50 transition-colors">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-lg font-bold text-slate-900">
                      {formatCurrency(payment.amount, {
                        locale: 'uz-UZ',
                        currency: bill.currency,
                      })}
                    </span>
                    <PaymentStatusBadge status={payment.status} />
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center justify-between text-slate-600">
                      <span>{t('date')}</span>
                      <span className="font-medium text-slate-900">
                        {payment.createdAt
                          ? format(new Date(payment.createdAt), 'MMM d, yyyy')
                          : '-'}
                      </span>
                    </div>
                    {payment.paymentMethod && (
                      <div className="flex items-center justify-between text-slate-600">
                        <span>{t('method')}</span>
                        <span className="font-medium text-slate-900">
                          {payment.paymentMethod.name}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">
            <CreditCard className="h-12 w-12 mx-auto mb-3 text-slate-300" />
            <p className="font-medium">{t('noPaymentsRecorded')}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function BillQuickActionsCard({ bill }: BillCardProps) {
  const { t } = useTranslation();
  return (
    <Card className="bg-white border-slate-200 shadow-sm hover:shadow-md transition-shadow md:col-span-2 lg:col-span-3">
      <CardHeader className="pb-3 border-b border-slate-100">
        <CardTitle className="text-lg font-semibold text-slate-900">
          {t('quickActions')}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {bill.contract && (
            <QuickLinkButton
              icon={FileCheck}
              label={t('viewContract')}
              to={`/app/contracts/${bill.contract.id}`}
            />
          )}
          {bill.contract?.client && (
            <QuickLinkButton
              icon={User}
              label={t('viewClient')}
              to={`/app/clients/${bill.contract.client.id}`}
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}
